import { z } from "zod";

// =====================================
// Spark Analysis
// =====================================

export const SparkApplicationInfoSchema = z.object({
  application_id: z.string(),
  cluster_id: z.string().nullable().optional(),
  application_name: z.string().nullable().optional(),
  start_time: z.number().nullable().optional(),
  end_time: z.number().nullable().optional(),
});

export const SparkStageMetricsSchema = z.object({
  stage_id: z.number(),
  name: z.string().nullable().optional(),
  tasks: z.number().default(0),
  run_ms: z.number().default(0),
  max_ms: z.number().default(0),
  avg_ms: z.number().default(0),
  min_ms: z.number().default(0),
  median_ms: z.number().default(0),
  q1_ms: z.number().default(0),
  q3_ms: z.number().default(0),
  stddev_ms: z.number().default(0),
  gc_ms: z.number().default(0),
  mem_spill: z.number().default(0),
  disk_spill: z.number().default(0),
  shuffle_read: z.number().default(0),
  shuffle_write: z.number().default(0),
  skew: z.number().default(0),
});

export type SparkStageMetrics = z.infer<typeof SparkStageMetricsSchema>;

export const SparkStageSummarySchema = z.object({
  stage_id: z.number(),
  tasks: z.number().default(0),
  duration_ms: z.number().default(0),
});

export type SparkStageSummary = z.infer<typeof SparkStageSummarySchema>;

export const SparkStageMetricSchema = z.object({
  stage_id: z.number(),
  stage_name: z.string().nullable().optional(),
  attempt_id: z.number().default(0),
  status: z.string().nullable().optional(),
  num_tasks: z.number().default(0),
  num_completed_tasks: z.number().default(0),
  num_failed_tasks: z.number().default(0),
  executor_run_time: z.number().default(0),
  executor_cpu_time: z.number().default(0),
  jvm_gc_time: z.number().default(0),
  input_bytes: z.number().default(0),
  input_records: z.number().default(0),
  output_bytes: z.number().default(0),
  output_records: z.number().default(0),
  shuffle_read_bytes: z.number().default(0),
  shuffle_read_records: z.number().default(0),
  shuffle_write_bytes: z.number().default(0),
  shuffle_write_records: z.number().default(0),
  memory_bytes_spilled: z.number().default(0),
  disk_bytes_spilled: z.number().default(0),
  peak_execution_memory: z.number().default(0),
  duration_ms: z.number().default(0),
  avg_ms: z.number().default(0),
  max_ms: z.number().default(0),
  min_ms: z.number().default(0),
  p50_ms: z.number().default(0),
  p75_ms: z.number().default(0),
  p90_ms: z.number().default(0),
  p95_ms: z.number().default(0),
  skew: z.number().default(0),
  gc_ratio: z.number().default(0),
  spill_ratio: z.number().default(0),
  data_read_per_task_bytes: z.number().default(0),
});

export const SparkStageMetricsPaginatedResponseSchema = z.object({
  items: z.array(SparkStageMetricSchema).default([]),
  total_count: z.number().default(0),
  page: z.number().default(1),
  page_size: z.number().default(20),
  total_pages: z.number().default(1),
});

export type SparkStageMetric = z.infer<typeof SparkStageMetricSchema>;
export type SparkStageMetricsPaginatedResponse = z.infer<
  typeof SparkStageMetricsPaginatedResponseSchema
>;

export const ComputeNodeSchema = z.object({
  label: z.string().nullable().optional(),
  is_driver: z.boolean().default(false),
  cpu_user: z.array(z.number()).default([]),
  cpu_system: z.array(z.number()).default([]),
  mem_used: z.array(z.number()).default([]),
  mem_swap: z.array(z.number()).default([]),
  gc_pct: z.array(z.number()).default([]),
});

export const ComputeMetricsSchema = z.object({
  times: z.array(z.string()).default([]),
  driver: ComputeNodeSchema.nullable().optional(),
  workers: z.array(ComputeNodeSchema).default([]),
  worker_count: z.number().default(0),
  node_type: z.string().nullable().optional(),
});

export type ComputeNode = z.infer<typeof ComputeNodeSchema>;
export type ComputeMetrics = z.infer<typeof ComputeMetricsSchema>;

export interface SparkJobSummary {
  job_id: number;
  duration_ms: number;
  failed: boolean;
  sql_execution_id?: number | null;
  description?: string | null;
  cost_cents: number;
}

export const SparkAnalysisSummaryResponseSchema = z.object({
  application: SparkApplicationInfoSchema,
  jobs: z
    .array(
      z.object({
        job_id: z.number(),
        duration_ms: z.number().default(0),
        failed: z.boolean().default(false),
        sql_execution_id: z.number().nullable().optional(),
        description: z.string().nullable().optional(),
        cost_cents: z.number().default(0),
      })
    )
    .default([]),
  job_sql_map: z.record(z.string(), z.number().nullable()).default({}),
  job_stages: z.record(z.string(), z.array(z.number())).default({}),
  sql_flags: z
    .record(
      z.string(),
      z.object({
        sql_id: z.number(),
        aqe: z.boolean().default(false),
        joins: z.number().default(0),
        scans: z.number().default(0),
        exchanges: z.number().default(0),
        collect_to_driver: z.boolean().default(false),
        no_partition_pruning: z.boolean().default(false),
        duplicate_scan: z.boolean().default(false),
        excessive_shuffle: z.boolean().default(false),
        cartesian_join: z.boolean().default(false),
        broadcast_nested_loop: z.boolean().default(false),
        global_sort: z.boolean().default(false),
      })
    )
    .default({}),
  sql_plan_dag: z
    .record(
      z.string(),
      z.array(
        z.object({
          id: z.number(),
          name: z.string(),
          category: z.string().default("other"),
          depth: z.number().default(0),
          parent_id: z.number().default(-1),
          detail: z.string().nullable().optional(),
        })
      )
    )
    .default({}),
  sql_stage_dag: z
    .record(
      z.string(),
      z.array(
        z.object({
          stg: z.number(),
          parents: z.array(z.number()).default([]),
          tasks: z.number().default(0),
          ms: z.number().default(0),
          name: z.string().default(""),
        })
      )
    )
    .default({}),
  stages: z
    .array(
      z.object({
        stage_id: z.number(),
        tasks: z.number().default(0),
        duration_ms: z.number().default(0),
      })
    )
    .default([]),
  stage_metrics: z.record(z.string(), SparkStageMetricsSchema).default({}),
  config: z.record(z.string(), z.string()).default({}),
  summary: z
    .object({
      total_jobs: z.number().default(0),
      total_stages: z.number().default(0),
      total_tasks: z.number().default(0),
      unique_sqls: z.number().default(0),
      failed_jobs: z.number().default(0),
      total_duration_ms: z.number().default(0),
      total_cost_cents: z.number().default(0),
    })
    .default({}),
  compute_metrics: ComputeMetricsSchema.nullable().optional(),
});

export type SparkApplicationInfo = z.infer<typeof SparkApplicationInfoSchema>;
export type SparkAnalysisSummaryResponse = z.infer<typeof SparkAnalysisSummaryResponseSchema>;

// Re-export opportunity and task types from split file
export {
  SparkOpportunitySchema,
  SparkOpportunitySummarySchema,
  SparkOpportunitiesResponseSchema,
  SparkTaskDetailSchema,
  SparkTasksPaginatedResponseSchema,
} from "./sparkOpportunityTypes";
export type {
  SparkOpportunity,
  SparkOpportunitiesResponse,
  SparkTaskDetail,
  SparkTasksPaginatedResponse,
} from "./sparkOpportunityTypes";
